import { assign } from 'lodash';
import API from '../api';
import * as actions from '../constants';
import { uploadImage } from './app';
import generateActions from './baseAction';

const {
  showError,
  clearErrors,
  getVehicleTypes,
  getVehicleType,
  removeVehicleType,
} = generateActions(
  'vehicle_type',
  'vehicle_types',
  'vehicles/types',
  'vehicleType',
  'vehicleTypes',
);

/**
 * Action to mark an add vehicle type request as sent
 * @param {Object} fields
 */
const requestAddVehicleType = (fields): any => ({
  fields,
  type: actions.REQUEST_ADD_VEHICLE_TYPE,
});

/**
 * Action to mark an add vehicle type request as received
 * @param {String|Boolean} error
 * @param {Object} data
 */
const receiveAddVehicleType = (error, data): any => ({
  error,
  data,
  type: actions.RECEIVE_ADD_VEHICLE_TYPE,
});

/**
 * Add a vehicle type
 * @param {Object} fields
 * @return {Promise}
 */
const addVehicleType = (fields): any => (dispatch) => {
  dispatch(requestAddVehicleType(fields));
  return new Promise((resolve, reject) => {
    dispatch(uploadImage(fields.image))
      .then((image) => {
        API.request('/vehicles/types', 'POST', assign({}, fields, { image }))
          .then((response) => response.json())
          .then((response) => {
            dispatch(receiveAddVehicleType(response.error, response.data));
            resolve(response);
          });
      })
      .catch(() => {
        dispatch(receiveAddVehicleType('Failed to upload image', {}));
        reject();
      });
  });
};

/**
 * Action to mark an update vehicle type request as sent
 * @param {Number} id
 */
const requestUpdateVehicleType = (id): any => ({
  id,
  type: actions.REQUEST_UPDATE_VEHICLE_TYPE,
});

/**
 * Action to mark an update vehicle type request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
const receiveUpdateVehicleType = (id, error, data): any => ({
  id,
  error,
  data,
  type: actions.RECEIVE_UPDATE_VEHICLE_TYPE,
});

/**
 * Update a vehicle type
 * @param {Number} id
 * @param {Object} fields
 * @return {Promise}
 */
const updateVehicleType = (id, fields): any => (dispatch) => {
  dispatch(requestUpdateVehicleType(id));

  // Attempt to upload the image if one specified
  return new Promise((resolve, reject) => {
    if (!fields.image) {
      resolve(fields);
      return;
    }
    dispatch(uploadImage(fields.image))
      .then((image) => {
        resolve(assign({}, fields, { image }));
      })
      .catch((err) => {
        dispatch(receiveUpdateVehicleType(id, err, {}));
        reject(err);
      });

    // Update the fields
  }).then((fieldsToUpdate: any) =>
    API.request(`/vehicles/types/${id}`, 'POST', fieldsToUpdate)
      .then((response) => response.json())
      .then((response) => {
        dispatch(receiveUpdateVehicleType(id, response.error, response.data));
        return response;
      }),
  );
};

/**
 * Action to mark an update vehicle type request as sent
 * @param {Number} id
 */
const requestGetHireCost = (id): any => ({
  id,
  type: actions.REQUEST_VEHICLE_TYPE_HIRE_COST,
});

/**
 * Action to mark an update vehicle type request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
const receiveGetHireCost = (id, error, data): any => ({
  id,
  error,
  data,
  type: actions.RECEIVE_VEHICLE_TYPE_HIRE_COST,
});

/**
 * Get the hire cost for a vehicle type
 * @param {Number} id
 * @param {Date} start
 * @param {Date} end
 */
const getHireCost = (id: number, start: Date, end: Date) => async (
  dispatch,
) => {
  dispatch(requestGetHireCost(id));

  let response: any = await API.request(
    `/vehicles/types/${id}/hire-cost?start=${start}&end=${end}`,
  );
  response = await response.json();
  dispatch(receiveGetHireCost(id, response.error, response.data));
  return response;
};

/**
 * Get the vehicles for a vehicle type
 */
const getVehiclesForVehicleType = (id: number) => async (dispatch) => {
  const response: any = await API.request(`/vehicles/types/${id}/vehicles`);
  return await response.json();
};

export {
  addVehicleType,
  clearErrors,
  getHireCost,
  getVehicleType,
  getVehicleTypes,
  removeVehicleType,
  showError,
  updateVehicleType,
  getVehiclesForVehicleType,
};
